// src/blob-hooks.ts
import { useCallback, useEffect, useState } from "react";
var urlCache = /* @__PURE__ */ new Map();
function useBlobUrl(hash, client) {
  const [url, setUrl] = useState(() => hash ? urlCache.get(hash) : void 0);
  useEffect(() => {
    if (!hash) {
      setUrl(void 0);
      return;
    }
    const cached = urlCache.get(hash);
    if (cached) {
      setUrl(cached);
      return;
    }
    let cancelled = false;
    setUrl(void 0);
    client.fetch(hash).then(async (res) => {
      if (cancelled || !res.ok) return;
      const objUrl = URL.createObjectURL(await res.blob());
      urlCache.set(hash, objUrl);
      if (!cancelled) setUrl(objUrl);
    }).catch(() => {
      if (!cancelled) setUrl(void 0);
    });
    return () => {
      cancelled = true;
    };
  }, [hash, client]);
  return url;
}
function useBlobQueue(queue) {
  const [pending, setPending] = useState(0);
  const [failed, setFailed] = useState([]);
  const drain = useCallback(async () => {
    const r = await queue.drain();
    setPending(await queue.pending());
    setFailed(r.failed);
    return r;
  }, [queue]);
  const enqueue = useCallback(
    async (key, bytes) => {
      await queue.enqueue(key, bytes);
      await drain();
    },
    [queue, drain]
  );
  useEffect(() => {
    void drain();
    const onOnline = () => {
      void drain();
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [drain]);
  return { pending, failed, enqueue, drain };
}

// src/factory.ts
import {
  ANON_IDENTITY,
  buildRowboatDb,
  storeName
} from "@jbroll/rowboat-client";
import { compileSchema as compileSchema2, relational as relational2 } from "@jbroll/rowboat-schema";
import {
  createContext,
  createElement,
  useContext,
  useEffect as useEffect3,
  useState as useState2
} from "react";

// src/hooks.ts
import { compileSchema, relational } from "@jbroll/rowboat-schema";
import { useEffect as useEffect2, useMemo, useRef, useSyncExternalStore } from "react";

// src/select.ts
function selectSource(store, selector, isEqual = Object.is) {
  let cache = null;
  return {
    subscribe: (cb) => store.subscribe(cb),
    getSnapshot: () => {
      const next = selector();
      if (cache && isEqual(cache.value, next)) return cache.value;
      cache = { value: next };
      return next;
    }
  };
}

// src/store.ts
function detachedRowboatStore(schema, db) {
  const names = storeTableNames(Object.keys(schema), db);
  const storeOf = names.storeOf;
  const storeName2 = (t) => resolveStoreName(names, t);
  const tables = [...storeOf.keys()];
  const schemaOf = new Map([...storeOf].map(([t, s]) => [s, t]));
  const snapshot = {};
  for (const t of tables) snapshot[t] = [];
  const subs = /* @__PURE__ */ new Set();
  const notify = () => {
    for (const cb of subs) cb();
  };
  const generation = {};
  const reload = async (t, live) => {
    generation[t] = (generation[t] ?? 0) + 1;
    const gen = generation[t];
    try {
      const store = storeName2(t);
      const rows = await db.table(store).filter((r) => !r.__deleted).toArray();
      if (!live() || generation[t] !== gen) return;
      snapshot[t] = rows.map((r) => db.decodeRow(store, r));
      notify();
    } catch (err) {
      const name = err?.name ?? "";
      if (!live() || name === "DatabaseClosedError" || /database has been closed/i.test(String(err)))
        return;
      throw err;
    }
  };
  return {
    all: (t) => {
      storeName2(t);
      return snapshot[t];
    },
    subscribe: (cb) => {
      subs.add(cb);
      return () => {
        subs.delete(cb);
      };
    },
    create: (t, row) => db.create(storeName2(t), row),
    update: (t, idOrItems, changes) => Array.isArray(idOrItems) ? db.update(storeName2(t), idOrItems) : db.update(storeName2(t), idOrItems, changes ?? {}),
    remove: (t, id) => db.remove(storeName2(t), id),
    attach: () => {
      let attached = true;
      const live = () => attached;
      const unsubscribe = db.subscribe((event) => {
        const t = schemaOf.get(event.table);
        if (t !== void 0) void reload(t, live);
      });
      const loaded = Promise.all(tables.map((t) => reload(t, live))).then(() => void 0);
      return {
        loaded,
        detach: () => {
          attached = false;
          unsubscribe();
        }
      };
    }
  };
}
function storeTableNames(tables, db) {
  const names = db.storeTables();
  const aboveCap = db.tablesAboveCap();
  const storeOf = /* @__PURE__ */ new Map();
  for (const t of tables) {
    const s = names.get(t);
    if (s !== void 0) storeOf.set(t, s);
    else if (!aboveCap.has(t))
      throw new Error(`rowboatStore: table ${t} has no store at this version`);
  }
  return { storeOf, aboveCap };
}
function resolveStoreName(names, t) {
  const s = names.storeOf.get(t);
  if (s !== void 0) return s;
  const needs = names.aboveCap.get(t);
  if (needs !== void 0)
    throw new Error(
      `rowboatStore: table ${t} needs schema version ${needs}; this store is capped below it`
    );
  throw new Error(`rowboatStore: ${t} is not a table of this schema`);
}
function rowboatStore(schema, db) {
  const { attach, ...store } = detachedRowboatStore(schema, db);
  const { loaded, detach } = attach();
  return { ...store, ready: loaded, dispose: detach };
}
function schemaEffectiveColumns(schema, db) {
  const effective = db.effectiveColumns();
  const out = /* @__PURE__ */ new Map();
  for (const [t, store] of storeTableNames(Object.keys(schema), db).storeOf) {
    const cols = effective.get(store);
    if (cols === void 0) throw new Error(`rowboat: store table ${store} has no columns`);
    out.set(t, cols);
  }
  return out;
}

// src/hooks.ts
function useRowboat(schema, source) {
  const owned = useMemo(
    () => "all" in source ? null : detachedRowboatStore(schema, source),
    [schema, source]
  );
  const store = owned ?? source;
  useEffect2(() => {
    if (!owned) return;
    return owned.attach().detach;
  }, [owned]);
  const compiled = useMemo(() => compileSchema(schema), [schema]);
  return useMemo(
    () => relational(
      schema,
      store,
      compiled,
      "all" in source ? void 0 : schemaEffectiveColumns(schema, source)
    ),
    [schema, store, compiled, source]
  );
}
function useSelect(g, selector, isEqual) {
  const store = g.$store;
  const selectorRef = useRef(selector);
  selectorRef.current = selector;
  const source = useMemo(
    () => selectSource(store, () => selectorRef.current(), isEqual),
    [store, isEqual]
  );
  return useSyncExternalStore(source.subscribe, source.getSnapshot, source.getSnapshot);
}

// src/factory.ts
var graphByName = /* @__PURE__ */ new Map();
var dbByName = /* @__PURE__ */ new Map();
var dbNameOf = (config) => config.identity ? storeName(config.name, config.identity) : config.name;
function getOrCreateGraph(schema, config) {
  const existing = graphByName.get(dbNameOf(config));
  if (existing) return existing;
  return buildGraph(schema, config, dbNameOf(config));
}
function buildGraph(schema, config, dbName) {
  const compiled = compileSchema2(schema);
  const db = buildRowboatDb(
    dbName,
    compiled.manifest,
    config.migrations ?? [],
    void 0,
    config.options
  );
  dbByName.set(dbName, db);
  const g = relational2(
    schema,
    rowboatStore(schema, db),
    compiled,
    schemaEffectiveColumns(schema, db)
  );
  graphByName.set(dbName, g);
  return g;
}
function evictGraph(name) {
  dbByName.get(name)?.close();
  dbByName.delete(name);
  graphByName.delete(name);
  const hold = holdByName.get(name);
  holdByName.delete(name);
  hold?.abort.abort(new Evicted(name));
  hold?.release?.();
}
var holdByName = /* @__PURE__ */ new Map();
var Evicted = class extends Error {
  constructor(name) {
    super(`graph ${name} was evicted while waiting for its lock`);
  }
};
function webLocks() {
  const locks = typeof navigator === "undefined" ? void 0 : navigator.locks;
  if (locks === void 0 || locks === null)
    throw new Error(
      "RowboatProvider: navigator.locks (the Web Locks API) is required to open the anonymous store"
    );
  return locks;
}
function heldGraph(schema, config) {
  const dbName = dbNameOf(config);
  const existing = graphByName.get(dbName);
  if (existing) return Promise.resolve(existing);
  const waiting = holdByName.get(dbName);
  if (waiting) return waiting.graph;
  const locks = webLocks();
  const abort = new AbortController();
  const hold = {
    abort,
    graph: new Promise((resolve, reject) => {
      locks.request(dbName, { mode: "shared", signal: abort.signal }, () => {
        if (abort.signal.aborted) throw abort.signal.reason;
        return new Promise((release) => {
          hold.release = release;
          resolve(buildGraph(schema, config, dbName));
        });
      }).catch((e) => {
        if (holdByName.get(dbName) === hold) holdByName.delete(dbName);
        reject(e);
      });
    })
  };
  holdByName.set(dbName, hold);
  return hold.graph;
}
function useGraph(schema, config) {
  const anon = config.identity === ANON_IDENTITY;
  const [g, setG] = useState2(() => {
    if (!anon) return getOrCreateGraph(schema, config);
    const held = graphByName.get(dbNameOf(config));
    return held === void 0 ? null : held;
  });
  const [failure, setFailure] = useState2(null);
  if (failure) throw failure.error;
  useEffect3(() => {
    if (g !== null) return;
    let live = true;
    const wait = () => {
      heldGraph(schema, config).then(
        (built) => {
          if (live) setG(built);
        },
        (e) => {
          if (!live) return;
          if (e instanceof Evicted) wait();
          else setFailure({ error: e });
        }
      );
    };
    try {
      wait();
    } catch (e) {
      setFailure({ error: e });
    }
    return () => {
      live = false;
    };
  }, [g]);
  return g;
}
function createRowboat(schema) {
  const Ctx = createContext(null);
  function RowboatProvider({
    config,
    children
  }) {
    const g = useGraph(schema, config);
    if (g === null) return null;
    return createElement(Ctx.Provider, { value: g }, children);
  }
  function useBoundRowboat() {
    const g = useContext(Ctx);
    if (!g) throw new Error("useRowboat() must be used inside <RowboatProvider>");
    return g;
  }
  function useBoundSelect(selector, isEqual) {
    return useSelect(useBoundRowboat(), selector, isEqual);
  }
  return { RowboatProvider, useRowboat: useBoundRowboat, useSelect: useBoundSelect };
}
export {
  createRowboat,
  evictGraph,
  rowboatStore,
  selectSource,
  useBlobQueue,
  useBlobUrl,
  useRowboat,
  useSelect
};
